/**
 * Platform Fee Service
 * API integration for platform fee tier management (financial module)
 *
 * @version 2025.1.0
 */

import apiClient from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface PlatformFee {
  id: number;
  minLives: number;
  maxLives: number | null;
  pricePerLife: number;
  isActive: boolean;
}

export interface PlatformFeeFormData {
  minLives: number;
  maxLives: number | null;
  pricePerLife: number;
  isActive?: boolean;
}

// ============================================================================
// SERVICE
// ============================================================================

class PlatformFeeService {
  private baseUrl = '/platform-fees';

  /**
   * List all platform fee tiers
   */
  async list(): Promise<PlatformFee[]> {
    const response = await apiClient.get(this.baseUrl);
    const responseData = response.data?.data || response.data;
    return Array.isArray(responseData) ? responseData : responseData?.fees || [];
  }

  /**
   * Create a new platform fee tier
   */
  async create(data: PlatformFeeFormData): Promise<PlatformFee> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data?.data || response.data;
  }

  /**
   * Update a platform fee tier
   */
  async update(id: number, data: PlatformFeeFormData): Promise<PlatformFee> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data?.data || response.data;
  }

  /**
   * Delete a platform fee tier
   */
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }
}

// Export singleton instance
export const platformFeeService = new PlatformFeeService();
export default platformFeeService;
